import React, { useContext, useMemo } from 'react';
import * as Icons from '@ant-design/icons';
import { Select, Space } from 'antd';
import { HeadPortal } from '@modules/layout/components/head-portal';
import { StyledHeaderButton } from '@modules/layout/styled';
import { StyledActionBar } from '@shared/styled/action-bar';
import { useBackgroundNavigate } from '@shared/hooks';
import { tableFilterQueryMaker } from '@shared/modules/next-table/utils';
import { useAuth } from '@modules/me';
import { useStatusesByModelId } from '@modules/statuses/hooks';
import { DiscountUsersTableContext } from '../context';
import { UsersService } from '../services';
import { getPersistenceUserRole, getUserRole, userQueryKeys } from '../utils';

export const DiscountUsersActionBar = () => {
  const navigate = useBackgroundNavigate();
  const { can } = useAuth();
  const { state, handleFetch, handleReset, handleSelectAll, handleResetSelection, handleChangeFilterById } = useContext(DiscountUsersTableContext);
  const selectionCount = Object.values(state.selectedRowIds).filter(Boolean).length;

  const declarationStatuses = useStatusesByModelId(2);

  const roleFilterValue = useMemo(() => getUserRole(state.filters.find((f) => f.id === userQueryKeys.role)?.value), [state.filters]);
  const declarationStateValue = useMemo(() => state.filters.find((f) => f.id === userQueryKeys.declarations.state.id)?.value ?? undefined, [state.filters]);

  const handleExport = async () => {
    const result = await UsersService.exportDiscountUsersExcel(tableFilterQueryMaker(state.filters));
    if (result.status === 200) {
      const url = URL.createObjectURL(result.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `discount_users_export_${Date.now()}.xls`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <>
      <HeadPortal>
        <StyledActionBar $flex={true}>
          <Space size={0}>
            <StyledHeaderButton type="text" onClick={() => navigate('/users')} icon={<Icons.ArrowLeftOutlined />}>
              İstifadəçilər
            </StyledHeaderButton>
            {!selectionCount ? (
              <StyledHeaderButton type="text" onClick={handleSelectAll} icon={<Icons.CheckCircleOutlined />}>
                Hamısını seç
              </StyledHeaderButton>
            ) : (
              <StyledActionBar.Selection>
                <Icons.CloseCircleTwoTone twoToneColor="#ff4d4f" onClick={handleResetSelection} style={{ cursor: 'pointer', fontSize: 16 }} role="icon" />
                <span>{selectionCount} seçilib</span>
              </StyledActionBar.Selection>
            )}
            <StyledHeaderButton type="text" onClick={handleFetch} icon={<Icons.ReloadOutlined />}>
              Yenilə
            </StyledHeaderButton>
            <StyledHeaderButton type="text" onClick={handleReset} icon={<Icons.ClearOutlined />}>
              Sıfırla
            </StyledHeaderButton>
            {!!state.data.length && (
              <StyledHeaderButton type="text" onClick={handleExport} icon={<Icons.DownloadOutlined />}>
                Export
              </StyledHeaderButton>
            )}
          </Space>
          {!selectionCount && (
            <Space size={0}>
              <StyledHeaderButton type="text" onClick={() => navigate('/statistics/qizil-onluq', { withBackground: false })} icon={<Icons.TrophyOutlined />}>
                Qızıl onluq
              </StyledHeaderButton>
              <StyledHeaderButton type="text" onClick={() => navigate('/statistics/users/general', { withBackground: true })} icon={<Icons.BarChartOutlined />} />
            </Space>
          )}
        </StyledActionBar>
      </HeadPortal>
      <Space size={8}>
        <Select
          style={{ width: 140 }}
          value={roleFilterValue}
          placeholder="İstifadəçi tipi"
          onChange={(val) => handleChangeFilterById(userQueryKeys.role, getPersistenceUserRole(val))}
          allowClear
          showSearch
        >
          {can('admins') && <Select.Option value="admin">Adminlər</Select.Option>}
          {can('admins') && <Select.Option value="warehouseman">Anbardarlar</Select.Option>}
          <Select.Option value="back-office">Back office</Select.Option>
        </Select>
        <Select
          style={{ minWidth: 140 }}
          mode="multiple"
          placeholder="Bağlama statusu"
          value={declarationStateValue}
          showSearch
          allowClear
          loading={declarationStatuses.isLoading}
          onChange={(val) => handleChangeFilterById(userQueryKeys.declarations.state.id, val?.length ? val : undefined)}
        >
          {declarationStatuses.data?.map((s) => (
            <Select.Option key={s.id} value={s.id.toString()}>
              {s.name}
            </Select.Option>
          ))}
        </Select>
      </Space>
    </>
  );
};
